import { InitialShadeState } from "..";
import { AssemblyStore, ExternalShadeData, IndoorFasciaSplit, IndoorRollDirection, IndoorShadeOptionsCoupledDict, ShadeStore } from "../types";
import StandardRoll from "../../../../assets/img/illus/standard_roll.png";
import ReverseRoll from "../../../../assets/img/illus/reverse_roll.png";

const IndoorShadeOptionsCoupled: (assembly: AssemblyStore, setAssembly: (assembly: AssemblyStore) => void, externalData: ExternalShadeData) => IndoorShadeOptionsCoupledDict = (assembly, setAssembly, externalData) => {

    const indoorFabrics = externalData.fabrics;

    const fabOptions = indoorFabrics.map(fab => ({ label: fab.name, value: fab.name, title: fab.name }));

    const setFabric = (newFabricName) => {
        setAssembly({
            ...assembly,
            shades: assembly.shades.map(s => ({
                ...s,
                fabric_name: newFabricName
            }))
        })
    }

    const setRollDirection = (newRollDirection) => {
        setAssembly({
            ...assembly,
            shades: assembly.shades.map(s => ({
                ...s,
                roll_direction: newRollDirection
            }))
        })
    }

    const headers = externalData.assemblyOptions.headers;
    const currentHeader = headers?.find(header => header.name === assembly.header_line);

    const isStandardRollAllowed = currentHeader?.single_standard_allowed ?? true;
    const isReverseRollAllowed = currentHeader?.single_reverse_allowed ?? true;

    const rollDirectionOptions = [
        { label: "Standard", value: "standard" as IndoorRollDirection, title: "Standard", isActive: isStandardRollAllowed },
        { label: "Reverse", value: "reverse" as IndoorRollDirection, title: "Reverse", isActive: isReverseRollAllowed }
    ];

    // 2-10 coupled shades
    const coupledAmountOptions = [
        {
            value: 2,
            label: "2",
            title: "2"
        },
        {
            value: 3,
            label: "3",
            title: "3"
        },
        {
            value: 4,
            label: "4",
            title: "4"
        },
        {
            value: 5,
            label: "5",
            title: "5"
        },
        {
            value: 6,
            label: "6",
            title: "6"
        },
        {
            value: 7,
            label: "7",
            title: "7"
        },
        {
            value: 8,
            label: "8",
            title: "8"
        },
        {
            value: 9,
            label: "9",
            title: "9"
        },
        {
            value: 10,
            label: "10",
            title: "10"
        }
    ];

    const isInsideCombinedFascia = externalData.quoteCombinations?.Fascias?.some(f => f.assemblies.some(a => a.sequence_id === assembly.sequence_id));
    const currentHeaderSku = currentHeader?.header_sku ?? "";
    const isMatchMullionHeader = ["RC3158", "RC4039", "RS-SC", "RS-SK"].some(headerSku => currentHeaderSku.includes(headerSku));
    const hasExtrusion = (currentHeader?.extrusion_splits?.length ?? 0) > 0;
    const fasciaSplitOptions = currentHeader?.extrusion_splits
    ?.filter(f => (!f.is_combined_override || (f.is_combined_override && isInsideCombinedFascia))
        && (f.value !== "match_mullion" || isMatchMullionHeader))
        .map(fascia => ({
            label: fascia.name,
            value: fascia.value as IndoorFasciaSplit,
            title: fascia.description,
            is_combined_override: fascia.is_combined_override
        })) ?? [];

    const setCoupledWidths = (newWidths: number[]) => {
        const shadesWithNewWidth = assembly.shades.map((shade) => {
            return {
                ...shade,
                width: newWidths[shade.column_coordinate] ?? 0.0
            }
        });

        setAssembly({
            ...assembly,
            shades: shadesWithNewWidth
        });
    };
    const setBandCount = (newBandCount: number) => {
        const currentBandCount = assembly?.band_count ?? 0;
        let newShades: ShadeStore[] = [];
        if (newBandCount > currentBandCount) {
            // Add
            // if(isDual)
            // Dual only file
            const frontShade = assembly?.shades.find(s => s.column_coordinate == 0 && s.row_coordinate == 0) ?? InitialShadeState();

            newShades = assembly?.shades
                .map(s => ({ ...s }))

            for (let i = 0; i < newBandCount; i++) {
                if (newShades.find(s => s.column_coordinate == i)) {
                    continue;
                }
                newShades.push({ ...frontShade, column_coordinate: i });
            }

        } else {
            // Delete
            newShades = assembly?.shades
                .filter(s => (s.column_coordinate + 1) <= newBandCount)
                .map(s => ({ ...s }))
        }

        newShades.forEach(ns => ns.width = 0);
        setAssembly({
            ...assembly,
            band_count: newBandCount,
            shades: newShades
        });
    }

    const indoorShadeOptionsCoupled: IndoorShadeOptionsCoupledDict = {
        Fabric: {
            label: "Fabric",
            options: fabOptions,
            clearValue: () => {
                setFabric("");
            },
            setValue: (value) => {
                setFabric(value);
            },
            getSelectedOption: () => {
                return fabOptions.find(fab => fab.value === assembly.shades[0]?.fabric_name) ?? { label: "", value: "", title: "" };
            },
            varName: "fabric_name",
            error: [],
            info: [],
            getValue: () => assembly.shades[0]?.fabric_name ?? "",
        },
        RollDirection: {
            label: "Roll Direction",
            options: rollDirectionOptions,
            clearValue: () => {
                setRollDirection("");
            },
            setValue: (value) => {
                setRollDirection(value);
            },
            getSelectedOption: () => {
                return rollDirectionOptions.find(roll => roll.value === assembly.shades[0]?.roll_direction) ?? { label: "", value: "" as IndoorRollDirection, title: "" };
            },
            varName: "roll_direction",
            error: [],
            info: [],
            getValue: () => (assembly.shades[0]?.roll_direction ?? "") as IndoorRollDirection,
            valuePopup: assembly.shades[0]?.roll_direction === "standard" ? StandardRoll : ReverseRoll
        },
        CoupledWidth: {
            label: "Coupled Width",
            options: [],
            clearValue: () => {
                setCoupledWidths([]);
            },
            setValue: (value) => {
                setCoupledWidths(value)
            },
            getSelectedOption: () => { return { label: "", value: [] as number[], title: "" } },
            varName: "coupled_width",
            error: [],
            info: [],
            getValue: () => {
                const columnTotal = assembly.shades.reduce((max, shade) => shade.column_coordinate > max ? shade.column_coordinate : max, 0);

                const widths = [] as number[];

                for (let i = 0; i <= columnTotal; i++) {
                    const colShade = assembly.shades.find(s => s.column_coordinate == i);
                    widths.push(colShade?.width ?? 0)
                }
                return widths
            }
        },
        BandCount: {
            label: "Band Count",
            options: coupledAmountOptions,
            clearValue: () => {
                setBandCount(2);
            },
            setValue: (value) => {
                setBandCount(value)
            },
            getSelectedOption: () => { return { label: "", value: assembly.band_count, title: "" } },
            varName: "band_count",
            error: [],
            info: [],
            getValue: () => assembly.band_count
        },
        FasciaSplit: {
            label: "Fascia Split",
            options: fasciaSplitOptions,
            clearValue: () => {
                setAssembly({
                    ...assembly,
                    fascia_split: "" as IndoorFasciaSplit
                });
            },
            setValue: (value) => {
                setAssembly({
                    ...assembly,
                    fascia_split: value
                });
            },
            getSelectedOption: () => {
                return fasciaSplitOptions.find(fascia => fascia.value === assembly.fascia_split) ?? { label: "", value: "" as IndoorFasciaSplit, title: "" };
            },
            varName: "fascia_split",
            error: [],
            info: [],
            getValue: () => assembly.fascia_split as IndoorFasciaSplit,
            active: hasExtrusion
        }

    };

    return indoorShadeOptionsCoupled;
};

export default IndoorShadeOptionsCoupled;